import { forwardRef } from 'react'
import DeviceTableHeader, { SortColumn, SortDirection } from './DeviceTableHeader'
import DeviceTableRow from './DeviceTableRow'
import { Device } from '@/models/device'
import { CustomField } from '@/models/customField'

interface DeviceTableProps {
  devices: Device[],
  customFields: CustomField[]
  canEditDevices: boolean
  columnWidths: number[]
  sortColumn: SortColumn
  sortDirection: SortDirection
  selectedDevices: Set<Device>
  onSort: (column: keyof Device) => void
  onSelectAll: () => void
  onSelectDevice: (device: Device) => void
  onEdit: (device: Device) => void
  onLog: (device: Device) => void
  onDelete: (device: Device) => void
  onResizeStart: (e: React.MouseEvent, index: number) => void
}

const DeviceTable = forwardRef<HTMLTableElement, DeviceTableProps>(
  function DeviceTable({
    devices,
    customFields,
    canEditDevices,
    columnWidths,
    sortColumn,
    sortDirection,
    selectedDevices,
    onSort,
    onSelectAll,
    onSelectDevice,
    onEdit,
    onLog,
    onDelete,
    onResizeStart
  }, ref) {
    const allSelected = selectedDevices.size === devices.length && devices.length > 0

    if (devices.length === 0) {
      return (
        <div className="no-devices">
          No devices match the current filters.
        </div>
      )
    }

    return (
      <div className="device-table-container">
        <table className="device-table" ref={ref}>
          <DeviceTableHeader
            customFields={customFields}
            canEditDevices={canEditDevices}
            columnWidths={columnWidths}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            allSelected={allSelected}
            onSort={onSort}
            onSelectAll={onSelectAll}
            onResizeStart={onResizeStart}
          />
          <tbody>
            {devices.map((device) => (
              <DeviceTableRow
                key={device.id}
                device={device}
                customFields={customFields}
                deviceSubCustomFields={device.subCustomFields}
                canEditDevices={canEditDevices}
                isSelected={[...selectedDevices].flatMap(m => m.id).includes(device.id)}
                onSelectDevice={onSelectDevice}
                onEdit={onEdit}
                onLog={onLog}
                onDelete={onDelete}
              />
            ))}
          </tbody>
        </table>
      </div>
    )
  }
)

export default DeviceTable
export type { SortColumn, SortDirection }